import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import type { CapabilityKey } from './usePropertyCapabilities';

export interface ServicePlan {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  price_cents: number;
  billing_interval: 'monthly' | 'annual';
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServicePlanWithCaps extends ServicePlan {
  capabilities: CapabilityKey[];
  subscription_count: number;
}

const SERVICE_PLANS_KEY = 'service-plans';

export function useServicePlans() {
  const { organization } = useApp();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [SERVICE_PLANS_KEY, orgId],
    queryFn: async () => {
      // Fetch plans
      const { data: plans, error } = await supabase
        .from('service_plans')
        .select('*')
        .eq('organization_id', orgId!)
        .order('is_default', { ascending: false })
        .order('name');
      if (error) throw error;

      if (!plans || plans.length === 0) return [] as ServicePlanWithCaps[];

      const planIds = plans.map((p: Record<string, unknown>) => (p as unknown as ServicePlan).id);

      // Fetch capabilities for all plans
      const { data: caps, error: capsError } = await supabase
        .from('service_plan_capabilities')
        .select('plan_id, capability')
        .in('plan_id', planIds);
      if (capsError) throw capsError;

      // Fetch subscription counts
      const { data: subs, error: subsError } = await supabase
        .from('property_service_subscriptions')
        .select('service_plan_id')
        .in('service_plan_id', planIds)
        .in('status', ['pending', 'active', 'trialing', 'past_due']);
      if (subsError) throw subsError;

      const capsByPlan = new Map<string, CapabilityKey[]>();
      for (const c of (caps ?? []) as Array<{ plan_id: string; capability: string }>) {
        const arr = capsByPlan.get(c.plan_id) ?? [];
        arr.push(c.capability as CapabilityKey);
        capsByPlan.set(c.plan_id, arr);
      }

      const countByPlan = new Map<string, number>();
      for (const s of (subs ?? []) as Array<{ service_plan_id: string }>) {
        countByPlan.set(s.service_plan_id, (countByPlan.get(s.service_plan_id) ?? 0) + 1);
      }

      return (plans as unknown as ServicePlan[]).map((p) => ({
        ...p,
        capabilities: capsByPlan.get(p.id) ?? [],
        subscription_count: countByPlan.get(p.id) ?? 0,
      })) as ServicePlanWithCaps[];
    },
    enabled: !!orgId,
  });
}

export interface CreatePlanInput {
  name: string;
  description?: string;
  price_cents: number;
  billing_interval: 'monthly' | 'annual';
  is_default?: boolean;
  capabilities: CapabilityKey[];
}

export function useCreateServicePlan() {
  const qc = useQueryClient();
  const { organization } = useApp();

  return useMutation({
    mutationFn: async (input: CreatePlanInput) => {
      if (!organization) throw new Error('No organization');

      // If setting as default, unset other defaults first
      if (input.is_default) {
        await supabase
          .from('service_plans')
          .update({ is_default: false })
          .eq('organization_id', organization.id)
          .eq('is_default', true);
      }

      const { data, error } = await supabase
        .from('service_plans')
        .insert({
          organization_id: organization.id,
          name: input.name,
          description: input.description ?? null,
          price_cents: input.price_cents,
          billing_interval: input.billing_interval,
          is_default: input.is_default ?? false,
        })
        .select('id')
        .single();
      if (error) throw error;

      // Insert capabilities
      if (input.capabilities.length > 0) {
        const { error: capError } = await supabase
          .from('service_plan_capabilities')
          .insert(input.capabilities.map((cap) => ({
            plan_id: data.id as string,
            capability: cap,
          })));
        if (capError) throw capError;
      }

      return data.id as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SERVICE_PLANS_KEY] });
    },
  });
}

export interface UpdatePlanInput {
  id: string;
  name?: string;
  description?: string | null;
  price_cents?: number;
  billing_interval?: 'monthly' | 'annual';
  is_default?: boolean;
  is_active?: boolean;
  capabilities?: CapabilityKey[];
}

export function useUpdateServicePlan() {
  const qc = useQueryClient();
  const { organization } = useApp();

  return useMutation({
    mutationFn: async (input: UpdatePlanInput) => {
      if (!organization) throw new Error('No organization');

      const { id, capabilities, ...updates } = input;

      // If setting as default, unset other defaults first
      if (updates.is_default) {
        await supabase
          .from('service_plans')
          .update({ is_default: false })
          .eq('organization_id', organization.id)
          .eq('is_default', true);
      }

      if (Object.keys(updates).length > 0) {
        const { error } = await supabase
          .from('service_plans')
          .update(updates)
          .eq('id', id);
        if (error) throw error;
      }

      // Replace capabilities if provided
      if (capabilities !== undefined) {
        const { error: delError } = await supabase
          .from('service_plan_capabilities')
          .delete()
          .eq('plan_id', id);
        if (delError) throw delError;

        if (capabilities.length > 0) {
          const { error: insError } = await supabase
            .from('service_plan_capabilities')
            .insert(capabilities.map((cap) => ({
              plan_id: id,
              capability: cap,
            })));
          if (insError) throw insError;
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SERVICE_PLANS_KEY] });
    },
  });
}

export function useDeactivateServicePlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      const { error } = await supabase
        .from('service_plans')
        .update({ is_active: false, is_default: false })
        .eq('id', planId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SERVICE_PLANS_KEY] });
    },
  });
}

export interface AssignPlanInput {
  property_id: string;
  service_plan_id: string;
  price_override_cents?: number;
}

export function useAssignServicePlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: AssignPlanInput) => {
      const { data, error } = await supabase
        .from('property_service_subscriptions')
        .insert({
          property_id: input.property_id,
          service_plan_id: input.service_plan_id,
          status: 'pending',
          price_override_cents: input.price_override_cents ?? null,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SERVICE_PLANS_KEY] });
      qc.invalidateQueries({ queryKey: ['property-capabilities'] });
      qc.invalidateQueries({ queryKey: [PROPERTY_SUBSCRIPTIONS_KEY] });
    },
  });
}

// ─── Property subscriptions query ──────────────────────────────────────────

const PROPERTY_SUBSCRIPTIONS_KEY = 'property-subscriptions';

export interface PropertySubscription {
  id: string;
  property_id: string;
  service_plan_id: string;
  status: string;
  price_override_cents: number | null;
  stripe_connect_customer_id: string | null;
  stripe_connect_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
  // Joined
  property_name?: string;
  plan_name?: string;
  plan_price_cents?: number;
  plan_billing_interval?: string;
}

export function usePropertySubscriptions() {
  const { organization } = useApp();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [PROPERTY_SUBSCRIPTIONS_KEY, orgId],
    queryFn: async () => {
      // Get all subscriptions for plans in this org
      const { data: plans } = await supabase
        .from('service_plans')
        .select('id')
        .eq('organization_id', orgId!);

      if (!plans || plans.length === 0) return [] as PropertySubscription[];

      const planIds = plans.map((p: Record<string, unknown>) => (p as { id: string }).id);

      const { data: subs, error } = await supabase
        .from('property_service_subscriptions')
        .select(`
          id, property_id, service_plan_id, status,
          price_override_cents, stripe_connect_customer_id,
          stripe_connect_subscription_id,
          current_period_start, current_period_end,
          cancel_at_period_end, created_at, updated_at
        `)
        .in('service_plan_id', planIds)
        .order('created_at', { ascending: false });
      if (error) throw error;

      if (!subs || subs.length === 0) return [] as PropertySubscription[];

      // Enrich with property name and plan details
      const propertyIds = [...new Set(subs.map((s: Record<string, unknown>) => (s as { property_id: string }).property_id))];
      const { data: properties } = await supabase
        .from('properties')
        .select('id, name')
        .in('id', propertyIds);

      const { data: planDetails } = await supabase
        .from('service_plans')
        .select('id, name, price_cents, billing_interval')
        .in('id', planIds);

      const propMap = new Map((properties ?? []).map((p: Record<string, unknown>) => [(p as { id: string }).id, (p as { name: string }).name]));
      const planMap = new Map((planDetails ?? []).map((p: Record<string, unknown>) => {
        const plan = p as { id: string; name: string; price_cents: number; billing_interval: string };
        return [plan.id, plan];
      }));

      return (subs as unknown as PropertySubscription[]).map((s) => {
        const plan = planMap.get(s.service_plan_id);
        return {
          ...s,
          property_name: propMap.get(s.property_id) ?? 'Unknown',
          plan_name: plan?.name ?? 'Unknown',
          plan_price_cents: plan?.price_cents ?? 0,
          plan_billing_interval: plan?.billing_interval ?? 'monthly',
        };
      });
    },
    enabled: !!orgId,
  });
}

// ─── Start billing (create Stripe checkout for a PSS row) ─────────────────

export interface StartBillingInput {
  subscription_id: string;
  property_id: string;
  customer_email: string;
  trial_days?: number;
}

export function useStartBilling() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: StartBillingInput) => {
      const result = await invokeAuthedFunction<{
        checkout_url?: string;
        platform_fee_cents?: number;
        installer_payout_cents?: number;
        error?: string;
      }>(supabase, 'create-customer-subscription', {
        property_id: input.property_id,
        subscription_id: input.subscription_id,
        customer_email: input.customer_email,
        trial_days: input.trial_days,
      });

      if (result.error) throw new Error(result.error);
      if (!result.checkout_url) throw new Error('No checkout URL returned');
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [SERVICE_PLANS_KEY] });
      qc.invalidateQueries({ queryKey: [PROPERTY_SUBSCRIPTIONS_KEY] });
    },
  });
}
